// R20 — WHAT AN ENTITLEMENTS PAYLOAD MEANS. ONE LAW, BOTH SIDES.
//
// THE DEFECT THIS EXISTS TO END, and it is one string wide:
//
//     server/tiers.js   featureSpec(f) -> FEATURES[f] || { tier: "vet", unregistered: true }
//     src/App.tsx       const lock = locks[feature]; if (!lock) { action(); return; }
//
// The server fails CLOSED on a key nobody registered. The client failed OPEN on the same key, and
// it could not have done otherwise: `lockPayloads` emits a row only for a feature that EXISTS and
// is REFUSED, so "she may open this" and "nobody has ever heard of this" arrived as the identical
// answer — an absent lock — and the surface mounted. No box, no error, no log.
//
// It is not hypothetical arithmetic. `shelves` is declared `enforcedIn: "shell"` in server/tiers.js:
// there is NO serving route behind that drawer, by ruling, because everything it lists is already
// free by another law. THE CLIENT IS ITS ONLY GATE. A rename on the server, a typo in the tap
// handler, either one — and the drawer opens for an account that bought nothing, with nothing
// anywhere recording that it happened.
//
// THE DISTINCTION THIS MODULE IS BUILT AROUND is R18's, met again in a different place: a payload
// that HAS NOT ARRIVED and a payload that ARRIVED WITHOUT THE KEY are not the same fact and must
// not be given the same answer.
//
//   • NOT ARRIVED  -> `pending`. It WAITS. W1-1's third state, and it is load-bearing: answering
//     "no" here flashes the tier box at a paying Vet on a slow network. The tap always lands; it
//     is only ever late.
//   • ARRIVED, key absent from the roster -> `unregistered`. That is an ANSWER, deliberately sent,
//     and it means exactly what the server means by it — closed.
//
// Imports nothing, so `scripts/r20-feature-keys.mjs` can load it and RUN it against the server's
// own `entitlementsFor` output for every tier.

// The four things a feature key can be, given what the client is holding.
export const FEATURE_OUTCOMES = ["pending", "open", "locked", "unregistered"];

// The roster of every feature the server knows about, read off an entitlements payload.
// `features` is a resolved yes/no per key over the WHOLE of FEATURES — it is the only field on the
// payload that names the keys a member is ALLOWED as well as the ones she is not, which is why the
// client has to keep it and not `locks` alone.
export function featureRoster(ent) {
  const features = ent && typeof ent === "object" ? ent.features : null;
  return features && typeof features === "object" ? Object.keys(features) : [];
}

// Normalise a raw /api/membership/entitlements response into the two fields this law reads, or
// null when there is nothing to read. A malformed body is NOT an answer — it is a payload that did
// not arrive, and it waits, exactly like a request still in flight.
export function featureEntitlements(payload) {
  if (!payload || typeof payload !== "object") return null;
  const features = payload.features && typeof payload.features === "object" ? payload.features : null;
  if (!features) return null;
  const locks = payload.locks && typeof payload.locks === "object" ? payload.locks : {};
  // >>> **HER ALLOTMENT RIDES THROUGH, BECAUSE THE SHELL DECIDES THE CHROME (2026-08-27).** <<<
  // Her funnel-containment law says a /talk arrival meets the conversation and NOTHING ELSE: no
  // menu, no rails, no chart-ready toast. That decision is made in the shell, and the shell had no
  // way to know she was a funnel arrival — the allotment was read only inside Ask Lala itself.
  // **IT IS CARRIED, NEVER RE-DERIVED**: this is the same object the counter and the wall are drawn
  // from, so the chrome and the meter can never disagree about whether she is mid-trial (R28/R29).
  // Absent on the payload it is simply absent here, which every reader below already treats as
  // *not a funnel arrival* — the safe direction, since guessing YES would strip the house's own
  // navigation from an ordinary member.
  return { features, locks, freeTurns: payload.freeTurns ?? null };
}

// THE ONE QUESTION, and it is the client's half of `canOpen`.
export function featureOutcome(ent, feature) {
  const key = String(feature || "");
  if (!ent || typeof ent !== "object" || !ent.features || typeof ent.features !== "object") return "pending";
  if (!Object.prototype.hasOwnProperty.call(ent.features, key)) return "unregistered";
  const locks = ent.locks && typeof ent.locks === "object" ? ent.locks : {};
  return locks[key] ? "locked" : "open";
}

// Only ONE outcome mounts a surface. Written this way round on purpose: a new outcome added later
// defaults to not-opening, which is the direction this whole round exists to move the client in.
export const opensSurface = (outcome) => outcome === "open";

// The box payload for a key that will not open, or null when it will. `pending` returns null too —
// the caller has to wait for the real answer rather than raise anything, and every caller in the
// client checks for the payload before it asks.
//
// An UNREGISTERED key has no lock row to hand over (the server never made one), so the box is
// composed from the key itself. That is the same minimal payload the network-failure path already
// raises, and it renders the generic ladder rather than naming a door that does not exist.
export function featureLockFor(ent, feature) {
  const outcome = featureOutcome(ent, feature);
  if (outcome === "locked") return ent.locks[String(feature || "")];
  if (outcome === "unregistered") return { feature: String(feature || "") };
  return null;
}

// THE SAME QUESTION ASKED AT A POINT WHERE WAITING IS NO LONGER ONE OF THE ANSWERS.
//
// `requireFeature` calls this on a request that has already come back. She has tapped; the tap has
// to land somewhere. So an entitlements body that arrived and could not be read is treated exactly
// as a network that could not answer at all is already treated one line below it — the box, not the
// surface. The server would refuse the request behind that surface anyway, so the honest local
// answer is the refusal, and the alternative is the defect this whole module exists to end: a
// missing field waving a paid door open.
//
// Kept as its own named function rather than a ternary at the call site, because "what happens when
// the payload is garbage" is a ruling, and a ruling that lives inside a component cannot be RUN by
// the fixture that is supposed to hold it.
export function featureLockAfterAnswer(ent, feature) {
  return ent ? featureLockFor(ent, feature) : { feature: String(feature || "") };
}
